import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription, filter, map } from 'rxjs';

@Component({
  selector: 'app-root',
  template: `
    <app-navbar></app-navbar>
    <main>
          <div id="chatContainer"></div>
      <router-outlet></router-outlet>
    </main>
    <app-footer></app-footer>
  `,
  styles: [`
    main {
      min-height: calc(100vh - 64px);
      margin-top: 64px;
    }
    @media (max-width: 991.98px) {
      main {
        min-height: calc(100vh - 56px);
        margin-top: 56px;
      }
    }
  `]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'Keyo AI';

  private static readonly DEFAULT_TITLE = 'Keyo AI - E-ticaret için Yapay Zeka Asistanı';

  private observer?: IntersectionObserver;
  private routerSub?: Subscription;
  private titleSub?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title
  ) {}

  /** Applies the `data.title` of the deepest activated route to the tab. */
  ngOnInit(): void {
    this.titleSub = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => {
          let r = this.route;
          while (r.firstChild) { r = r.firstChild; }
          return r.snapshot.data['title'] as string | undefined;
        })
      )
      .subscribe((t) => this.titleService.setTitle(t ?? AppComponent.DEFAULT_TITLE));
  }

  ngAfterViewInit(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    // Ratio thresholds can never be met by elements much taller than the viewport
    // (e.g. stacked pricing cards on mobile), so reveal on first intersection.
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          this.observer?.unobserve(entry.target);
        }
      }
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });

    const wireUp = () => {
      const candidates = document.querySelectorAll<HTMLElement>(
        'main section:not(.ds-page-hero):not(.ds-hiw):not(.ds-contact):not(.ds-legal), .ds-hiw__split, .ds-hiw__service, .ds-feature, .ds-testimonial, .ds-about__stat, .price-card'
      );
      candidates.forEach((el) => {
        if (el.classList.contains('is-visible')) return;

        if (!el.classList.contains('ds-reveal')) {
          el.classList.add('ds-reveal');
        }

        if (this.isRevealInView(el)) {
          el.classList.add('is-visible');
          return;
        }

        this.observer!.observe(el);
      });
    };

    wireUp();
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => setTimeout(wireUp, 0));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.routerSub?.unsubscribe();
    this.titleSub?.unsubscribe();
  }

  /** Mirrors IntersectionObserver threshold / rootMargin for instant above-the-fold reveal. */
  private isRevealInView(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const rootBottom = vh * 0.9;
    return rect.bottom > 0 && rect.top < rootBottom;
  }
}
